import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { api } from '../../context/AuthContext';

const PR_TYPE_LABELS = {
  company_news: 'Company News',
  product_launch: 'Product Launch',
  event: 'Event',
  partnership: 'Partnership',
  award: 'Award',
  milestone: 'Milestone',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export default function PressReleasePage() {
  const [activeTab, setActiveTab] = useState('create');
  const [topic, setTopic] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [prType, setPrType] = useState('company_news');
  const [status, setStatus] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [prData, setPrData] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const { data: res } = await api.get('/press-releases/');
      setHistory(res.prs || []);
    } catch (error) {
      setHistoryError(errDetail(error));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    setStatus('');
    try {
      const { data: res } = await api.post('/press-releases/generate', {
        topic,
        company_name: companyName,
        pr_type: prType,
      });
      setStatus('Generated successfully!');
      setPrData(res.data);
      loadHistory();
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleView = (pr) => {
    setPrData(pr);
    setActiveTab('create');
  };

  const handleDelete = async (pr) => {
    if (!window.confirm(`Delete press release "${pr.topic}"?`)) return;
    try {
      await api.delete(`/press-releases/${pr.id}`);
      if (prData?.id === pr.id) setPrData(null);
      setHistory((prev) => prev.filter((x) => x.id !== pr.id));
    } catch (error) {
      setHistoryError(errDetail(error));
    }
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Press Releases</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Draft AP-style releases and plan distribution.</p>
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        <button
          type="button"
          className={`pb-2 px-1 text-sm ${activeTab === 'create' ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
          onClick={() => setActiveTab('create')}
        >
          Create
        </button>
        <button
          type="button"
          className={`pb-2 px-1 text-sm ${activeTab === 'history' ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
          onClick={() => {
            setActiveTab('history');
            loadHistory();
          }}
        >
          History {history.length > 0 && <span className="ml-1 text-xs text-slate-400">({history.length})</span>}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        {activeTab === 'create' && (
          <form onSubmit={handleGenerate} className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Corp"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Topic / Announcement</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Launched a new AI tool"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">PR Type</label>
              <select
                value={prType}
                onChange={(e) => setPrType(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              >
                {Object.entries(PR_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={isGenerating}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isGenerating && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {isGenerating ? 'Generating…' : 'Generate Press Release'}
            </button>
          </form>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Your Press Releases</h3>
              <button
                type="button"
                onClick={loadHistory}
                className="text-xs text-violet-600 hover:text-violet-700"
              >
                Refresh
              </button>
            </div>

            {historyLoading && <p className="text-sm text-slate-500">Loading…</p>}
            {historyError && (
              <div className="p-3 rounded-md text-sm bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                {historyError}
              </div>
            )}
            {!historyLoading && !historyError && history.length === 0 && (
              <p className="text-sm text-slate-500">No press releases yet. Create one to get started.</p>
            )}

            {history.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="py-2 pr-4">Topic</th>
                      <th className="py-2 pr-4">Company</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((pr) => (
                      <tr key={pr.id} className="border-b border-slate-100 dark:border-slate-900">
                        <td className="py-2 pr-4 text-slate-800 dark:text-slate-200">{pr.topic}</td>
                        <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">{pr.company_name}</td>
                        <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">{PR_TYPE_LABELS[pr.type] || pr.type}</td>
                        <td className="py-2 pr-4 text-slate-500">{formatDate(pr.created_at)}</td>
                        <td className="py-2 pr-4">
                          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {pr.status}
                          </span>
                        </td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleView(pr)}
                            className="text-violet-600 hover:text-violet-700 mr-3"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(pr)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {status && activeTab === 'create' && (
          <div className={`mt-4 p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}

        {prData && activeTab === 'create' && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
              {prData.topic ? `${prData.company_name}: ${prData.topic}` : 'Generated Press Release'}
            </h3>
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md border border-slate-200 dark:border-slate-800 prose prose-sm prose-slate dark:prose-invert max-w-none">
              <ReactMarkdown>{prData.content}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
